<template>
  <v-card class="base-card shadow">
    <v-card-title class="headline accent--text pb-4">
      {{ title }}
    </v-card-title>

    <v-card-text>
      <CardRow>
        <v-checkbox
          class="rounded-lg mini-text field-edit d-inline-block"
          v-model="dissertation.isPHD"
          label="Диссертация PHD"
          hide-details
          dense
          :disabled="isShowMode"
          @change="updateDissertation()"
        />
      </CardRow>

      <CardAutocompleteRow
        v-model="dissertation.dissertationDegree"
        :items="dissertationDegreeList"
        item-value="id"
        item-text="name"
        label="Кандидатская/Докторская"
        :disabled="isShowMode"
        :error-messages="errorMessages.dissertationDegree"
        :required="dissertationFieldRequired"
        @input="
          $v.dissertation.dissertationDegree.$touch()
          updateDissertation()
        "
      />

      <CardAutocompleteRow
        v-model="dissertation.step"
        :items="scienceStepList"
        item-value="id"
        item-text="name"
        label="Аспирант/Докторант"
        :disabled="isShowMode"
        :error-messages="errorMessages.step"
        :required="dissertationFieldRequired"
        @input="
          $v.dissertation.step.$touch()
          updateDissertation()
        "
      />

      <CardFieldRow
        v-model="dissertation.commonWork.workName"
        label="Название диссертации"
        placeholder="Добавьте название диссертации"
        :disabled="isShowMode"
        :error-messages="errorMessages.commonWork.workName"
        :required="dissertationFieldRequired"
        @input="
          $v.dissertation.commonWork.workName.$touch()
          updateDissertation()
        "
      />

      <CardAutocompleteRow
        v-if="!dissertation.notFoundPreparationPlace"
        v-model="dissertation.preparationPlace"
        :items="protectionPlaceList"
        item-value="id"
        item-text="fullName"
        label="Организация, где подготовлена диссертация"
        placeholder="Выберите организацию"
        :disabled="isShowMode"
        :no-filter="false"
        @input="updateDissertation()"
      />

      <CardFieldRow
        v-else
        v-model="dissertation.preparationPlaceStr"
        label="Организация, где подготовлена диссертация"
        placeholder="Добавьте организацию"
        :disabled="isShowMode"
        :error-messages="errorMessages.preparationPlaceStr"
        @input="$v.dissertation.preparationPlaceStr.$touch(), updateDissertation()"
      />

      <CardRow>
        <v-checkbox
          class="rounded-lg mini-text field-edit d-inline-block"
          v-model="dissertation.notFoundPreparationPlace"
          label="Нет в списке"
          hide-details
          dense
          :disabled="isShowMode"
          @change="resetPreparationPlace(), updateDissertation()"
        />
      </CardRow>

      <CardAutocompleteRow
        v-if="!dissertation.notFoundProtectionPlace"
        v-model="dissertation.protectionPlace"
        :items="protectionPlaceList"
        item-value="id"
        item-text="fullName"
        label="Место защиты диссертации (организация)"
        placeholder="Выберите место защиты"
        :disabled="isShowMode"
        :error-messages="errorMessages.protectionPlace"
        :required="dissertationFieldRequired"
        :no-filter="false"
        @input="
          $v.dissertation.protectionPlace.$touch()
          updateDissertation()
        "
      />

      <CardFieldRow
        v-else
        v-model="dissertation.protectionPlaceStr"
        label="Место защиты диссертации (организация)"
        placeholder="Добавьте место защиты"
        :disabled="isShowMode"
        :error-messages="errorMessages.protectionPlaceStr"
        :required="dissertationFieldRequired"
        @input="
          $v.dissertation.protectionPlaceStr.$touch()
          updateDissertation()
        "
      />

      <CardRow>
        <v-checkbox
          class="rounded-lg mini-text field-edit d-inline-block"
          v-model="dissertation.notFoundProtectionPlace"
          label="Нет в списке"
          hide-details
          dense
          :disabled="isShowMode"
          @change="resetProtectionPlace(), updateDissertation()"
        />
      </CardRow>

      <CardDateRow
        v-model="dissertation.protectionDate"
        label="Дата защиты"
        placeholder="Добавьте дату защиты"
        :disabled="isShowMode"
        :error-messages="errorMessages.protectionDate"
        :required="dissertationFieldRequired"
        @input="
          $v.dissertation.protectionDate.$touch()
          updateDissertation()
        "
      />

      <CardFieldRow
        v-model="dissertation.diplomaSeries"
        label="Серия диплома"
        placeholder="Добавьте серию диплома"
        :disabled="isShowMode"
        :error-messages="errorMessages.diplomaSeries"
        :required="dissertationFieldRequired"
        @input="
          $v.dissertation.diplomaSeries.$touch()
          updateDissertation()
        "
      />

      <CardFieldRow
        v-model="dissertation.diplomaNumber"
        type="number"
        label="Номер диплома"
        placeholder="Добавьте номер диплома"
        :disabled="isShowMode"
        :error-messages="errorMessages.diplomaNumber"
        :required="dissertationFieldRequired"
        @input="
          $v.dissertation.diplomaNumber.$touch()
          updateDissertation()
        "
      />

      <CardDateRow
        v-model="dissertation.issueDate"
        label="Дата выдачи диплома"
        placeholder="Добавьте дату выдачи"
        :disabled="isShowMode"
        :error-messages="errorMessages.issueDate"
        :required="dissertationFieldRequired"
        @input="
          $v.dissertation.issueDate.$touch()
          updateDissertation()
        "
      />

      <CardDateRow
        v-model="dissertation.councilDate"
        label="Дата совета"
        placeholder="Добавьте дату совета"
        :disabled="isShowMode"
        :error-messages="errorMessages.councilDate"
        :required="dissertationFieldRequired"
        @input="
          $v.dissertation.councilDate.$touch()
          updateDissertation()
        "
      />

      <CardFieldRow
        v-model="dissertation.specialityNum"
        label="Номер специальности в дисс. совете"
        placeholder="Добавьте номер специальности"
        hide-border-bottom
        :disabled="isShowMode"
        :error-messages="errorMessages.specialityNum"
        :required="dissertationFieldRequired"
        @input="
          $v.dissertation.specialityNum.$touch()
          updateDissertation()
        "
      />
    </v-card-text>
  </v-card>
</template>

<script>
import { Dissertation } from '@/models'
import CardRow from '@/components/ui/row/CardRow'
import CardAutocompleteRow from '../../ui/row/CardAutocompleteRow'
import { setValidateMessages } from '../../../scripts/validation'
import CardFieldRow from '../../ui/row/CardFieldRow'
import { maxLength, requiredIf } from 'vuelidate/lib/validators'
import { checkDate } from '@/scripts/validation'
import CardDateRow from '../../ui/row/CardDateRow'
import { mapGetters } from 'vuex'

export default {
  components: { CardDateRow, CardFieldRow, CardAutocompleteRow, CardRow },
  props: {
    isShowMode: Boolean,
    selectedItem: Object,
    title: String
  },

  data: () => ({
    dissertation: new Dissertation()
  }),

  computed: {
    ...mapGetters('dictionaries', ['dissertationDegreeList', 'protectionPlaceList', 'scienceStepList']),

    dissertationFieldRequired() {
      return !this.isShowMode && !this.dissertation.isPHD
    },

    errorMessages() {
      const allErrors = {}

      Object.keys(this.$v.dissertation.$params).forEach(key => {
        allErrors[key] = setValidateMessages(this.$v.dissertation[key])
      })

      Object.keys(this.$v.dissertation.commonWork.$params).forEach(key => {
        allErrors.commonWork[key] = setValidateMessages(this.$v.dissertation.commonWork[key])
      })

      return allErrors
    }
  },

  validations() {
    return {
      dissertation: {
        commonWork: {
          workName: { required: requiredIf(() => !this.dissertation.isPHD), maxLength: maxLength(500) }
        },

        dissertationDegree: { required: requiredIf(item => !item.isPHD) },
        step: { required: requiredIf(item => !item.isPHD) },
        preparationPlaceStr: { maxLength: maxLength(300) },
        protectionPlace: { required: requiredIf(item => !item.notFoundProtectionPlace && !item.isPHD) },
        protectionPlaceStr: { required: requiredIf(item => item.notFoundProtectionPlace && !item.isPHD) },
        protectionDate: { required: requiredIf(item => !item.isPHD), checkDate },
        diplomaSeries: { required: requiredIf(item => !item.isPHD), maxLength: maxLength(4) },
        diplomaNumber: { required: requiredIf(item => !item.isPHD), maxLength: maxLength(6) },
        issueDate: { required: requiredIf(item => !item.isPHD), checkDate },
        councilDate: { required: requiredIf(item => !item.isPHD) },
        specialityNum: { required: requiredIf(item => !item.isPHD), maxLength: maxLength(20) }
      }
    }
  },

  methods: {
    updateInvalid() {
      this.$emit('updateInvalid', this.$v.$invalid)
    },

    resetPreparationPlace() {
      this.dissertation.preparationPlaceStr = null
      this.dissertation.preparationPlace = null
    },

    resetProtectionPlace() {
      this.dissertation.protectionPlaceStr = null
      this.dissertation.protectionPlace = null
    },

    updateDissertation() {
      this.$emit('update:selected-item', new Dissertation(this.dissertation.getForAPI()))
      this.updateInvalid()
    }
  },

  watch: {
    selectedItem: {
      immediate: true,
      deep: true,
      handler(val) {
        this.dissertation = new Dissertation(val.getForAPI())
        this.updateInvalid()
      }
    }
  }
}
</script>

<style scoped></style>
